import React, {
	createContext,
	FC,
	ReactNode,
	RefCallback,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

export type AutopilotReviewSurfaceKind = 'REVIEWER' | 'OBSTRUCTION';

export interface AutopilotReviewSurfaceEntry {
	element: HTMLElement;
	id: string;
	kind: AutopilotReviewSurfaceKind;
}

interface RegistryValue {
	entries: AutopilotReviewSurfaceEntry[];
	register: (entry: AutopilotReviewSurfaceEntry) => void;
	unregister: (id: string) => void;
}

const NOOP_REGISTRY: RegistryValue = {
	entries: [],
	register: () => undefined,
	unregister: () => undefined,
};

const AutopilotReviewSurfaceContext = createContext<RegistryValue>(NOOP_REGISTRY);

let surfaceSequence = 0;

export const AutopilotReviewSurfaceProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [entriesById, setEntriesById] = useState<Record<string, AutopilotReviewSurfaceEntry>>({});
	const register = useCallback((entry: AutopilotReviewSurfaceEntry) => {
		setEntriesById((current) =>
			current[entry.id]?.element === entry.element && current[entry.id]?.kind === entry.kind
				? current
				: { ...current, [entry.id]: entry },
		);
	}, []);
	const unregister = useCallback((id: string) => {
		setEntriesById((current) => {
			if (!current[id]) return current;
			const next = { ...current };
			delete next[id];
			return next;
		});
	}, []);
	const value = useMemo<RegistryValue>(
		() => ({ entries: Object.values(entriesById), register, unregister }),
		[entriesById, register, unregister],
	);
	return (
		<AutopilotReviewSurfaceContext.Provider value={value}>
			{children}
		</AutopilotReviewSurfaceContext.Provider>
	);
};

export const useAutopilotReviewSurfaces = (): AutopilotReviewSurfaceEntry[] =>
	useContext(AutopilotReviewSurfaceContext).entries;

export const useAutopilotReviewSurfaceRef = <ElementType extends HTMLElement = HTMLElement>(
	kind: AutopilotReviewSurfaceKind,
	enabled = true,
): RefCallback<ElementType> => {
	const { register, unregister } = useContext(AutopilotReviewSurfaceContext);
	const idRef = useRef<string>();
	const elementRef = useRef<ElementType | null>(null);
	if (!idRef.current) {
		surfaceSequence += 1;
		idRef.current = `autopilot-review-surface-${surfaceSequence}`;
	}
	const id = idRef.current;

	useEffect(
		() => () => {
			unregister(id);
		},
		[id, unregister],
	);

	useEffect(() => {
		if (!enabled || !elementRef.current) {
			unregister(id);
			return;
		}
		register({ element: elementRef.current, id, kind });
	}, [enabled, id, kind, register, unregister]);

	return useCallback(
		(element: ElementType | null) => {
			elementRef.current = element;
			if (!enabled || !element) {
				unregister(id);
				return;
			}
			register({ element, id, kind });
		},
		[enabled, id, kind, register, unregister],
	);
};
